import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConsentCategories, hasDecision, setConsent } from "../../core/consent";
import LegalDocDialog from "../legal/LegalDocDialog";
import { clearLegalHash, legalDocStem, setLegalHash } from "../legal/legalHash";
import CookieConsentBanner from "./CookieConsentBanner";
import CookiePreferencesDialog from "./CookiePreferencesDialog";

type PolicyKind = "cookie" | "privacy";

interface CookieConsentContextValue {
    /** Open the granular preferences dialog (used by the "Cookie settings" link). */
    openPreferences: () => void;
    /** Open a legal policy document in the app-wide dialog. */
    openPolicy: (kind: PolicyKind) => void;
    /**
     * Real rendered height (px) of the bottom banner while it's visible, 0
     * otherwise. A consumer that has fixed-position content near the
     * bottom of its own viewport (the marketing site's hero CTA, its
     * mobile sticky CTA) can reserve this much space so the banner never
     * ends up on top of it.
     */
    bannerHeight: number;
}

const CookieConsentContext = createContext<CookieConsentContextValue>({
    openPreferences: () => undefined,
    openPolicy: () => undefined,
    bannerHeight: 0
});

const buildPolicyUrl = (kind: PolicyKind) => (locale: string): string =>
    `/legal/${legalDocStem(kind)}.${locale}.md`;

/** Normalise an i18next language (e.g. "en-GB") to a bare locale code. */
const toDocLocale = (lng?: string): string => (lng ? lng.split("-")[0].toLowerCase() : "en");

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t, i18n } = useTranslation("CookieConsent");
    // The playground is measured too (GA4 tag in playground.html), so it asks
    // for the same decision as the site; the consent log call it makes hits
    // the mock transport and is simply dropped there.
    const [showBanner, setShowBanner] = useState<boolean>(() => !hasDecision());
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [policy, setPolicy] = useState<{ open: boolean; kind: PolicyKind }>({ open: false, kind: "cookie" });
    const bannerRef = useRef<HTMLDivElement | null>(null);
    const [bannerHeight, setBannerHeight] = useState(0);

    const bannerVisible = showBanner && !prefsOpen;

    useEffect(() => {
        if (!bannerVisible) {
            setBannerHeight(0);
            return;
        }
        const node = bannerRef.current;
        if (!node || typeof ResizeObserver === "undefined") return;
        const measure = () => setBannerHeight(node.getBoundingClientRect().height);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [bannerVisible]);

    const locale = toDocLocale(i18n.language);

    const decide = useCallback(
        (categories: ConsentCategories) => {
            setConsent(categories, locale);
            setShowBanner(false);
            setPrefsOpen(false);
        },
        [locale]
    );

    const openPreferences = useCallback(() => setPrefsOpen(true), []);
    const openPolicy = useCallback((kind: PolicyKind) => {
        setPolicy({ open: true, kind });
        setLegalHash(kind);
    }, []);
    const closePolicy = useCallback(() => {
        setPolicy((p) => ({ ...p, open: false }));
        clearLegalHash();
    }, []);

    const value = useMemo<CookieConsentContextValue>(
        () => ({ openPreferences, openPolicy, bannerHeight }),
        [openPreferences, openPolicy, bannerHeight]
    );

    return (
        <CookieConsentContext.Provider value={value}>
            {children}

            {/* Hide the banner while the granular preferences dialog is open —
                otherwise both the bottom banner and the centred dialog show at
                once. The banner reappears if the dialog is closed without a
                decision (showBanner stays true until `decide` runs). */}
            {bannerVisible ? (
                <CookieConsentBanner
                    rootRef={bannerRef}
                    onAcceptAll={() => decide({ analytics: true, marketing: true, preferences: true })}
                    onRejectAll={() => decide({ analytics: false, marketing: false, preferences: false })}
                    onCustomize={openPreferences}
                    onOpenPolicy={openPolicy}
                />
            ) : null}

            <CookiePreferencesDialog open={prefsOpen} onClose={() => setPrefsOpen(false)} onSave={decide} />

            <LegalDocDialog
                open={policy.open}
                title={policy.kind === "cookie" ? t("banner.cookiePolicy") : t("banner.privacyPolicy")}
                locale={locale}
                buildUrl={buildPolicyUrl(policy.kind)}
                onClose={closePolicy}
            />
        </CookieConsentContext.Provider>
    );
};

export const useCookieConsent = (): CookieConsentContextValue => useContext(CookieConsentContext);
